import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const routes = [
  // ── Public ─────────────────────────────────────────────────
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { public: true },
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/PricingView.vue'),
    meta: { public: true },
  },
  {
    path: '/mcp',
    name: 'mcp',
    component: () => import('@/views/McpView.vue'),
    meta: { public: true },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/PrivacyView.vue'),
    meta: { public: true },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsView.vue'),
    meta: { public: true },
  },
  {
    path: '/refund',
    name: 'refund',
    component: () => import('@/views/RefundView.vue'),
    meta: { public: true },
  },
  {
    // App Store Connect requires a reachable Support URL for the iOS listing.
    path: '/support',
    name: 'support',
    component: () => import('@/views/SupportView.vue'),
    meta: { public: true },
  },
  // ── SEO landing pages (public, prerendered) ────────────────
  {
    path: '/receipt-scanner',
    name: 'seoReceiptScanner',
    component: () => import('@/views/seo/ReceiptScannerView.vue'),
    meta: { public: true },
  },
  {
    path: '/pdf-to-csv',
    name: 'seoPdfToCsv',
    component: () => import('@/views/seo/PdfToCsvView.vue'),
    meta: { public: true },
  },
  {
    path: '/pdf-to-excel',
    name: 'seoPdfToExcel',
    component: () => import('@/views/seo/PdfToExcelView.vue'),
    meta: { public: true },
  },
  {
    path: '/how-to-save-pdf-as-excel',
    name: 'seoHowToSavePdfAsExcel',
    component: () => import('@/views/seo/HowToSavePdfAsExcelView.vue'),
    meta: { public: true },
  },
  {
    path: '/how-to-convert-pdf-form-to-excel',
    name: 'seoHowToConvertPdfFormToExcel',
    component: () => import('@/views/seo/HowToConvertPdfFormToExcelView.vue'),
    meta: { public: true },
  },
  {
    path: '/intelligent-character-recognition',
    name: 'seoIcr',
    component: () => import('@/views/seo/IcrView.vue'),
    meta: { public: true },
  },
  {
    path: '/invoice-ocr',
    name: 'seoInvoiceOcr',
    component: () => import('@/views/seo/InvoiceOcrView.vue'),
    meta: { public: true },
  },
  {
    path: '/receipt-scanner-app',
    name: 'seoReceiptScannerApp',
    component: () => import('@/views/seo/ReceiptScannerAppView.vue'),
    meta: { public: true },
  },
  {
    path: '/how-to-organize-receipts',
    name: 'seoHowToOrganizeReceipts',
    component: () => import('@/views/seo/HowToOrganizeReceiptsView.vue'),
    meta: { public: true },
  },
  {
    path: '/scan-receipts-and-organize',
    name: 'seoScanReceiptsAndOrganize',
    component: () => import('@/views/seo/ScanReceiptsAndOrganizeView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, hideLayout: true, redirectIfAuthed: true },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/SignupView.vue'),
    meta: { public: true, hideLayout: true, redirectIfAuthed: true },
  },
  {
    path: '/verify',
    name: 'verify',
    component: () => import('@/views/VerifyView.vue'),
    meta: { public: true, hideLayout: true },
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true, hideLayout: true },
  },

  // ── Authenticated ──────────────────────────────────────────
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/upload',
    name: 'upload',
    component: () => import('@/views/UploadView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/documents/:id',
    name: 'documentResult',
    component: () => import('@/views/ResultView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('@/views/HistoryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Org-invite landing (signup-first) — the emailed invite link. Public:
    // it validates the token itself and handles signed-in users inline.
    path: '/invite',
    name: 'invite',
    component: () => import('@/views/InviteView.vue'),
    meta: { public: true },
  },
  {
    path: '/api-docs',
    name: 'apiDocs',
    component: () => import('@/views/ApiDocsView.vue'),
    meta: { public: true },
  },
  {
    // Enterprise org invite accept (E3). Auth required — the backend matches
    // the invite to the signed-in account's email.
    path: '/org/accept',
    name: 'orgAccept',
    component: () => import('@/views/OrgAcceptView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Internal accuracy dashboard. Server gates by allowlist (404 for others);
    // the route is reachable by URL but reveals nothing without a valid admin token.
    path: '/admin/accuracy',
    name: 'adminAccuracy',
    component: () => import('@/views/AdminAccuracyView.vue'),
    meta: { requiresAuth: true },
  },

  // ── Fallback ───────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  if (to.meta?.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }
  if (to.meta?.redirectIfAuthed && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
})

export default router
